import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Zap,
  CheckCircle2,
  XCircle,
  Activity,
  Layers,
  Target as TargetIcon,
  Shield,
  History,
  ChevronDown,
  Clock,
  Trophy,
} from "lucide-react";
import { Skeleton } from "./ui/skeleton";
import { CollapseToggle } from "./CollapseToggle";
import { useCollapsed } from "../hooks/useCollapsed";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtNum = (n, d = 2) =>
  n == null
    ? "—"
    : Number(n).toLocaleString("en-IN", { maximumFractionDigits: d });

const toIST = (isoUtcStr) => {
  // Convert ISO UTC string -> "HH:MM" in IST (UTC+5:30), 24-hour
  if (!isoUtcStr) return "—";
  try {
    const d = new Date(isoUtcStr);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleTimeString("en-GB", {
      timeZone: "Asia/Kolkata",
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const STATUS_STYLE = {
  HIT_TARGET: { c: "var(--bull)", icon: CheckCircle2, label: "TARGET" },
  HIT_SL: { c: "var(--bear)", icon: XCircle, label: "SL" },
  EXPIRED: { c: "var(--text-muted)", icon: Clock, label: "EXPIRED" },
  PENDING: { c: "var(--warn)", icon: Activity, label: "PENDING" },
};

const StatusPill = ({ status }) => {
  const cfg = STATUS_STYLE[status] || STATUS_STYLE.PENDING;
  const Icon = cfg.icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-mono-data border"
      style={{ color: cfg.c, borderColor: cfg.c, background: "transparent" }}
    >
      <Icon size={11} />
      {cfg.label}
    </span>
  );
};

const PerformanceBanner = ({ perf }) => {
  if (!perf || perf.fired === 0) return null;
  const winColor =
    perf.win_rate_pct >= 60
      ? "var(--bull)"
      : perf.win_rate_pct >= 40
      ? "var(--warn)"
      : "var(--bear)";
  return (
    <div
      data-testid="oi-performance-banner"
      className="border-grid surface px-4 py-3 flex flex-wrap items-center gap-4"
      style={{ background: "rgba(0,230,118,0.04)" }}
    >
      <div className="flex items-center gap-2">
        <Trophy size={14} style={{ color: winColor }} />
        <div className="text-[10px] uppercase tracking-[0.2em] font-mono-data text-muted-2">
          {perf.days}-Day Performance
        </div>
      </div>
      <div className="flex items-center gap-4 text-xs font-mono-data flex-1 flex-wrap">
        <span>
          <span className="text-muted-3">Fired</span>{" "}
          <span className="font-semibold">{perf.fired}</span>
        </span>
        <span style={{ color: "var(--bull)" }}>
          <span className="opacity-70">Hits</span>{" "}
          <span className="font-semibold">{perf.hits}</span>
        </span>
        <span style={{ color: "var(--bear)" }}>
          <span className="opacity-70">Losses</span>{" "}
          <span className="font-semibold">{perf.losses}</span>
        </span>
        <span className="text-muted-2">
          <span className="opacity-70">Expired</span>{" "}
          <span className="font-semibold">{perf.expired}</span>
        </span>
        <span style={{ color: "var(--warn)" }}>
          <span className="opacity-70">Pending</span>{" "}
          <span className="font-semibold">{perf.pending}</span>
        </span>
      </div>
      <div
        className="font-mono-data text-base font-bold tracking-tight"
        style={{ color: winColor }}
      >
        {perf.win_rate_pct}%{" "}
        <span className="text-[10px] uppercase tracking-wider opacity-70">
          win rate
        </span>
      </div>
    </div>
  );
};

const ENTRY_QUALITY_STYLE = {
  FRESH: {
    c: "var(--bull)",
    bg: "var(--bull-muted)",
    label: "FRESH",
    hint: "Near VWAP · early OI buildup",
  },
  MOMENTUM: {
    c: "var(--warn)",
    bg: "rgba(255,159,10,0.10)",
    label: "MOMENTUM",
    hint: "Moving but not extended",
  },
  CHASING: {
    c: "var(--bear)",
    bg: "var(--bear-muted)",
    label: "CHASING",
    hint: "Already extended · wait for pullback",
  },
};

const EntryQualityBadge = ({ quality, vwap_distance_pct }) => {
  if (!quality) return null;
  const cfg = ENTRY_QUALITY_STYLE[quality] || ENTRY_QUALITY_STYLE.MOMENTUM;
  return (
    <div
      className="inline-flex flex-col items-start gap-0.5 px-2 py-1 border"
      style={{ color: cfg.c, borderColor: cfg.c, background: cfg.bg }}
      title={cfg.hint}
    >
      <span className="text-[10px] uppercase tracking-wider font-mono-data font-semibold">
        {cfg.label}
      </span>
      <span className="text-[9px] font-mono-data opacity-80">
        VWAP {vwap_distance_pct >= 0 ? "+" : ""}
        {fmtNum(vwap_distance_pct)}%
      </span>
    </div>
  );
};

const WeakSignalBadge = ({ weak, reasons }) => {
  if (!weak) return null;
  const hint = (reasons || []).join(" · ") || "Below quality threshold";
  return (
    <div
      className="inline-flex items-center gap-1 px-2 py-1 border text-[10px] uppercase tracking-wider font-mono-data font-semibold"
      style={{ color: "var(--warn, #d19a66)", borderColor: "var(--warn, #d19a66)", background: "rgba(209,154,166,0.08)" }}
      title={hint}
      data-testid="oi-weak-signal-badge"
    >
      <Zap size={10} />
      Weak Signal
    </div>
  );
};

const TRADE_TYPE_STYLE = {
  INTRADAY: { c: "var(--bull)", bg: "var(--bull-muted)" },
  BTST: { c: "var(--warn)", bg: "rgba(255,159,10,0.10)" },
  SWING: { c: "#0a84ff", bg: "rgba(10,132,255,0.10)" },
  POSITIONAL: { c: "#bf5af2", bg: "rgba(191,90,242,0.10)" },
};

const TradeTypeBadge = ({ trade_type }) => {
  if (!trade_type) return null;
  const cfg = TRADE_TYPE_STYLE[trade_type.type] || TRADE_TYPE_STYLE.SWING;
  return (
    <div
      className="inline-flex flex-col items-start gap-0.5 px-2 py-1 border"
      style={{ color: cfg.c, borderColor: cfg.c, background: cfg.bg }}
    >
      <span className="text-[10px] uppercase tracking-wider font-mono-data font-semibold">
        {trade_type.type}
      </span>
      <span className="text-[9px] font-mono-data opacity-80">
        {trade_type.horizon}
      </span>
    </div>
  );
};

const SectorBadge = ({ sector }) => {
  if (!sector?.name) return null;
  const pct = sector.trend?.change_pct ?? 0;
  const positive = pct > 0;
  const color = positive ? "var(--bull)" : pct < 0 ? "var(--bear)" : "var(--text-muted)";
  return (
    <div
      className="inline-flex items-center gap-1 px-2 py-1 border text-[10px] font-mono-data"
      style={{ color, borderColor: color, background: "transparent" }}
      title={`Sector: ${sector.name}`}
    >
      <Layers size={10} />
      <span className="uppercase tracking-wider">{sector.name}</span>
      <span className="opacity-80">
        {positive ? "+" : ""}
        {fmtNum(pct)}%
      </span>
    </div>
  );
};

const TFBadge = ({ label, trend }) => {
  const map = {
    BULLISH: { c: "var(--bull)", bg: "var(--bull-muted)", Icon: TrendingUp },
    BEARISH: { c: "var(--bear)", bg: "var(--bear-muted)", Icon: TrendingDown },
    NEUTRAL: { c: "var(--text-muted)", bg: "rgba(255,255,255,0.04)", Icon: Minus },
  };
  const cfg = map[trend] || map.NEUTRAL;
  const { Icon } = cfg;
  return (
    <div
      className="flex flex-col items-center justify-center gap-0.5 px-2 py-1.5 border"
      style={{ color: cfg.c, borderColor: cfg.c, background: cfg.bg }}
    >
      <div className="text-[9px] uppercase tracking-wider font-mono-data opacity-70">
        {label}
      </div>
      <Icon size={12} />
    </div>
  );
};

const Card = ({ s, idx }) => (
  <div
    data-testid={`oi-suggestion-${idx + 1}`}
    className="border-grid surface flex flex-col hover:bg-white/[0.02] transition-colors"
  >
    <div className="px-5 py-4 border-b border-grid flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div
          className="w-9 h-9 flex items-center justify-center"
          style={{
            background: "var(--bull-muted)",
            color: "var(--bull)",
            border: "1px solid var(--bull)",
          }}
        >
          <TrendingUp size={16} />
        </div>
        <div>
          <div className="font-heading text-base font-semibold leading-tight">
            {s.company_name || s.symbol}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
            {s.symbol} · F&amp;O
          </div>
        </div>
      </div>
      <div className="flex flex-col items-end gap-1.5">
        <EntryQualityBadge
          quality={s.entry_quality}
          vwap_distance_pct={s.vwap_distance_pct ?? 0}
        />
        <TradeTypeBadge trade_type={s.trade_type} />
        <WeakSignalBadge weak={s.weak_signal} reasons={s.weak_signal_reasons} />
      </div>
    </div>

    {/* Sector trend chip */}
    {s.sector && (
      <div className="px-5 py-2 border-b border-grid flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data flex items-center gap-1">
          <Clock size={10} /> Sector Trend
        </div>
        <SectorBadge sector={s.sector} />
      </div>
    )}

    <div className="px-5 py-4 grid grid-cols-3 gap-3 border-b border-grid">
      <div>
        <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
          Price
        </div>
        <div className="font-mono-data text-base font-semibold">
          ₹{fmtNum(s.last_price)}
        </div>
        <div
          className="text-[10px] font-mono-data mt-0.5"
          style={{ color: "var(--bull)" }}
        >
          +{fmtNum(s.change_pct)}%
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
          OI Change
        </div>
        <div
          className="font-mono-data text-base font-semibold"
          style={{ color: "var(--bull)" }}
        >
          +{fmtNum(s.oi_change_pct)}%
        </div>
        <div className="text-[10px] font-mono-data text-muted-3 mt-0.5">
          {fmtNum(s.oi_change, 0)} contracts
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
          VWAP
        </div>
        <div className="font-mono-data text-base font-semibold">
          ₹{fmtNum(s.vwap)}
        </div>
        <div
          className="text-[10px] font-mono-data mt-0.5"
          style={{ color: "var(--bull)" }}
        >
          ABOVE
        </div>
      </div>
    </div>

    {/* Multi-Timeframe Confirmation */}
    {s.multi_tf && (
      <div className="px-5 py-3 border-b border-grid flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
          Multi-TF
        </div>
        <div className="flex gap-1.5">
          <TFBadge label="5M" trend={s.multi_tf.tf_5m?.trend} />
          <TFBadge label="1H" trend={s.multi_tf.tf_1h?.trend} />
          <TFBadge label="1D" trend={s.multi_tf.tf_daily?.trend} />
        </div>
        <div
          className="font-mono-data text-xs"
          style={{
            color:
              s.multi_tf.bullish_count >= 2
                ? "var(--bull)"
                : "var(--text-muted)",
          }}
        >
          {s.multi_tf.bullish_count}/3
        </div>
      </div>
    )}

    {/* Support / Resistance */}
    {s.levels && (
      <div className="px-5 py-3 border-b border-grid grid grid-cols-2 gap-3">
        <div className="flex items-center gap-2">
          <Shield size={12} style={{ color: "var(--bear)" }} />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
              Support
            </div>
            <div
              className="font-mono-data text-sm font-semibold"
              style={{ color: "var(--bear)" }}
            >
              ₹{fmtNum(s.levels.support)}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <TargetIcon size={12} style={{ color: "var(--bull)" }} />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
              Resistance
            </div>
            <div
              className="font-mono-data text-sm font-semibold"
              style={{ color: "var(--bull)" }}
            >
              ₹{fmtNum(s.levels.resistance)}
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Buy Zone + Trade Plan */}
    {s.trade && (
      <div className="px-5 py-3 border-b border-grid">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data mb-2">
          Buy Zone
        </div>
        <div className="font-mono-data text-sm font-semibold mb-2" style={{ color: "var(--bull)" }}>
          ₹{fmtNum(s.trade.buy_zone_low)} — ₹{fmtNum(s.trade.buy_zone_high)}
        </div>
        <div className="grid grid-cols-3 gap-2 text-[11px]">
          <div>
            <div className="text-[9px] uppercase tracking-wider text-muted-3 font-mono-data">
              Entry
            </div>
            <div className="font-mono-data font-semibold">₹{fmtNum(s.trade.entry)}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-muted-3 font-mono-data">
              SL
            </div>
            <div
              className="font-mono-data font-semibold"
              style={{ color: "var(--bear)" }}
            >
              ₹{fmtNum(s.trade.stop_loss)}
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-muted-3 font-mono-data">
              Target
            </div>
            <div
              className="font-mono-data font-semibold"
              style={{ color: "var(--bull)" }}
            >
              ₹{fmtNum(s.trade.target)}
            </div>
          </div>
        </div>
      </div>
    )}

    <div className="px-5 py-4 bg-white/[0.015] flex-1">
      <div className="flex items-center gap-2 mb-2">
        <Layers size={12} style={{ color: "var(--warn)" }} />
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
          Option Suggestion
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
            ATM CE
          </div>
          <div className="font-mono-data text-lg font-semibold">
            {s.option.atm_strike}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
            ITM CE
          </div>
          <div className="font-mono-data text-lg font-semibold">
            {s.option.itm_strike}
          </div>
        </div>
      </div>
      <p className="text-xs text-muted-2 mt-2">
        {s.option.recommendation}
      </p>
    </div>

    <div className="px-5 py-3 border-t border-grid flex items-center justify-between text-[10px] font-mono-data text-muted-3">
      <div>
        RSI <span style={{ color: "var(--text-primary)" }}>{fmtNum(s.rsi, 1)}</span>
      </div>
      {s.trade && (
        <div>
          R:R{" "}
          <span style={{ color: s.trade.risk_reward >= 1.5 ? "var(--bull)" : "var(--warn)" }}>
            1:{fmtNum(s.trade.risk_reward, 2)}
          </span>
        </div>
      )}
      <div>
        Score{" "}
        <span style={{ color: "var(--bull)" }}>{fmtNum(s.score, 1)}</span>
      </div>
    </div>
    {s.scan_time_ist && (
      <div
        data-testid={`oi-suggestion-scan-time-${idx + 1}`}
        className="px-5 py-2 border-t border-grid flex items-center gap-1.5 text-[10px] font-mono-data text-muted-3"
        style={{ background: "rgba(255,255,255,0.015)" }}
      >
        <Clock size={10} />
        <span className="uppercase tracking-wider">Scan Time</span>
        <span className="ml-auto" style={{ color: "var(--text-primary)" }}>
          {s.scan_time_ist}
        </span>
      </div>
    )}
  </div>
);

const NiftyStatus = ({ nifty }) => {
  if (!nifty) return null;
  const ok = nifty.stable_above_support;
  const Icon = ok ? CheckCircle2 : XCircle;
  const color = ok ? "var(--bull)" : "var(--bear)";
  return (
    <div
      data-testid="nifty-stability-badge"
      className="inline-flex items-center gap-2 px-3 py-1.5 border font-mono-data text-xs uppercase tracking-wider"
      style={{
        color,
        borderColor: color,
        background: ok ? "var(--bull-muted)" : "var(--bear-muted)",
      }}
    >
      <Icon size={12} />
      Nifty {ok ? "stable" : "weak"} · {nifty.change_pct >= 0 ? "+" : ""}
      {fmtNum(nifty.change_pct)}%
    </div>
  );
};

export const OISuggestions = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, toggle] = useCollapsed("oi-suggestions");
  const [historyCollapsed, toggleHistory] = useCollapsed("oi-history", true);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const load = async (force = false) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/oi-suggestions`, {
        params: force ? { force: "true" } : {},
      });
      setData(res.data);
    } catch (e) {
      /* ignore – UI handles empty state */
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/oi-suggestions/history?days=3`);
      setHistory(res.data);
    } catch (e) {
      /* ignore */
    } finally {
      setHistoryLoading(false);
    }
  };

  // 2026-09-23 (chat: "instead of auto-refresh...keep Refresh button...
  // that might reduce upstox calls") — this used to poll /api/oi-
  // suggestions every 5 minutes regardless of whether anyone had the page
  // open, adding to the same aggregate Upstox/Cloudflare rate pressure the
  // scheduler-side gate work earlier today was fighting. Now loads once on
  // mount and only again via the explicit Refresh button below.
  useEffect(() => {
    load();
  }, []);

  // Load history lazily when user first expands it
  useEffect(() => {
    if (!historyCollapsed && history === null && !historyLoading) {
      loadHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyCollapsed]);

  return (
    <section data-testid="oi-suggestions-section" className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.25em] text-muted-3 font-mono-data uppercase">
            02 / OI Long-Buildup
          </div>
          <h2 className="font-heading text-xl md:text-2xl font-bold mt-1">
            Stock Option Suggestions
          </h2>
          <p className="text-xs text-muted-3 mt-1">
            Top 10 F&amp;O stocks with rising OI <strong>and</strong> price, holding above VWAP
            (2026-08: price-down or below-VWAP candidates are no longer shown — that's short-buildup
            territory, not long-buildup) · click Refresh for the latest scan. Cards flagged{" "}
            <span style={{ color: "var(--warn, #d19a66)" }}>Weak Signal</span> passed the direction
            check but are thin on volume (RVOL) or the broader Nifty context — still shown for
            transparency, just lower conviction.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <NiftyStatus nifty={data?.nifty} />
          <button
            data-testid="oi-refresh-button"
            onClick={() => load(true)}
            disabled={loading}
            className="border-grid surface px-3 py-1.5 text-[10px] uppercase tracking-wider font-mono-data hover:bg-white/5 disabled:opacity-50"
          >
            {loading ? "↻ Loading…" : "↻ Refresh"}
          </button>
          <CollapseToggle
            collapsed={collapsed}
            onToggle={toggle}
            testId="oi-suggestions-toggle"
          />
        </div>
      </div>

      {!collapsed && (<>
      {!data?.available && !loading ? (
        <div className="border-grid surface p-8 text-center">
          <Activity size={20} className="mx-auto mb-2 text-muted-3" />
          <p className="text-sm text-muted-2">
            {data?.reason || "OI data temporarily unavailable. Will retry in 5 minutes."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          {loading && !data
            ? Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="border-grid surface p-5 space-y-3">
                  <Skeleton className="h-6 w-32" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-16 w-full" />
                </div>
              ))
            : (data?.suggestions || []).map((s, idx) => (
                <Card key={s.symbol} s={s} idx={idx} />
              ))}
        </div>
      )}

      {data?.available && data.suggestions.length === 0 && (
        <div className="border-grid surface p-6 text-center">
          <p className="text-sm text-muted-2">
            {data?.market_status && !data.market_status.is_open
              ? `Market is closed. ${data.market_status.next_open_hint || ""} Currently ${data.market_status.now_ist}.`
              : `No qualifying long-buildup setups right now — scanned ${data.scanned} F&O stocks.`}
          </p>
        </div>
      )}

      {/* History sub-panel (3 days, collapsible) */}
      <div className="border-grid surface" data-testid="oi-history-panel">
        <button
          data-testid="oi-history-toggle"
          onClick={toggleHistory}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/[0.02] transition-colors"
          aria-expanded={!historyCollapsed}
        >
          <div className="flex items-center gap-2">
            <History size={14} style={{ color: "var(--text-secondary)" }} />
            <div className="text-[11px] uppercase tracking-[0.2em] font-mono-data text-muted-2">
              History · Last 3 Days
            </div>
            {history?.total != null && (
              <span
                className="ml-2 px-2 py-0.5 text-[10px] font-mono-data border"
                style={{
                  color: history.total > 0 ? "var(--bull)" : "var(--text-muted)",
                  borderColor: history.total > 0 ? "var(--bull)" : "var(--border-color)",
                }}
              >
                {history.total}
              </span>
            )}
          </div>
          <ChevronDown
            size={16}
            className="transition-transform"
            style={{
              transform: historyCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
              color: "var(--text-secondary)",
            }}
          />
        </button>

        {!historyCollapsed && (
          <div className="border-t border-grid">
            {history?.performance && (
              <div className="px-4 pt-4">
                <PerformanceBanner perf={history.performance} />
              </div>
            )}
            {historyLoading && history === null ? (
              <div className="p-4 space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : (history?.grouped || []).length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-2">
                No suggestions fired in the last 3 days yet — history will
                populate as the scanner identifies setups.
              </div>
            ) : (
              <div className="divide-y divide-grid">
                {history.grouped.map((g) => (
                  <div key={g.date} data-testid={`oi-history-day-${g.date}`}>
                    <div
                      className="px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-mono-data text-muted-3"
                      style={{ background: "rgba(255,255,255,0.02)" }}
                    >
                      {g.date} · {g.items.length} suggestion
                      {g.items.length > 1 ? "s" : ""}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-grid text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
                            <th className="text-left px-4 py-2">Time IST</th>
                            <th className="text-left px-4 py-2">Symbol</th>
                            <th className="text-left px-4 py-2">Strategy</th>
                            <th className="text-left px-4 py-2">Status</th>
                            <th className="text-right px-4 py-2">Price</th>
                            <th className="text-right px-4 py-2">Δ%</th>
                            <th className="text-right px-4 py-2">15m OI%</th>
                            <th className="text-right px-4 py-2">Entry</th>
                            <th className="text-right px-4 py-2">SL</th>
                            <th className="text-right px-4 py-2">Target</th>
                            <th className="text-right px-4 py-2">Option</th>
                            <th className="text-right px-4 py-2">Score</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.items.map((it, i) => (
                            <tr
                              key={`${it.symbol}-${it.fired_at}-${i}`}
                              data-testid={`oi-history-row-${i}`}
                              className="border-b border-grid hover:bg-white/[0.02]"
                            >
                              <td className="px-4 py-2 font-mono-data text-muted-2">
                                {toIST(it.fired_at)}
                              </td>
                              <td className="px-4 py-2 font-mono-data font-semibold">
                                {it.symbol}
                              </td>
                              <td className="px-4 py-2 font-mono-data">
                                {it.trade_type?.type ? (
                                  <span
                                    className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border"
                                    style={{
                                      color:
                                        it.trade_type.type === "INTRADAY"
                                          ? "var(--bull)"
                                          : it.trade_type.type === "SWING"
                                          ? "#7AB6FF"
                                          : "#FFB66E",
                                      borderColor:
                                        it.trade_type.type === "INTRADAY"
                                          ? "var(--bull)"
                                          : it.trade_type.type === "SWING"
                                          ? "#7AB6FF"
                                          : "#FFB66E",
                                    }}
                                  >
                                    {it.trade_type.type}
                                  </span>
                                ) : (
                                  <span className="text-muted-3">—</span>
                                )}
                              </td>
                              <td className="px-4 py-2">
                                <StatusPill status={it.status || "PENDING"} />
                              </td>
                              <td className="px-4 py-2 font-mono-data text-right">
                                ₹{fmtNum(it.trigger_price)}
                              </td>
                              <td
                                className="px-4 py-2 font-mono-data text-right"
                                style={{ color: "var(--bull)" }}
                              >
                                +{fmtNum(it.change_pct)}%
                              </td>
                              <td
                                className="px-4 py-2 font-mono-data text-right"
                                style={{ color: "var(--bull)" }}
                              >
                                {it.oi_15m_pct != null
                                  ? `+${fmtNum(it.oi_15m_pct)}%`
                                  : "—"}
                              </td>
                              <td className="px-4 py-2 font-mono-data text-right">
                                ₹{fmtNum(it.entry)}
                              </td>
                              <td
                                className="px-4 py-2 font-mono-data text-right"
                                style={{ color: "var(--bear)" }}
                              >
                                ₹{fmtNum(it.stop_loss)}
                              </td>
                              <td
                                className="px-4 py-2 font-mono-data text-right"
                                style={{ color: "var(--bull)" }}
                              >
                                ₹{fmtNum(it.target)}
                              </td>
                              <td className="px-4 py-2 font-mono-data text-right">
                                {it.option?.atm_strike} {it.option?.type}
                              </td>
                              <td
                                className="px-4 py-2 font-mono-data text-right"
                                style={{ color: "var(--bull)" }}
                              >
                                {fmtNum(it.score, 1)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
                <div className="px-4 py-2 flex justify-end">
                  <button
                    data-testid="oi-history-refresh"
                    onClick={loadHistory}
                    disabled={historyLoading}
                    className="text-[10px] uppercase tracking-wider font-mono-data text-muted-2 hover:text-white disabled:opacity-50"
                  >
                    {historyLoading ? "↻ Loading…" : "↻ Refresh history"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      </>)}
    </section>
  );
};